import { useState, useEffect, useMemo } from 'react'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Search,
  FileText,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { OrganizationContext } from './AssistPage'
import { getFirebaseDb } from '@/lib/firebase'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  CodeBlock,
  InlineCode,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Heading,
  Blockquote,
  UnorderedList,
  OrderedList,
  ListItem,
  Paragraph,
  Anchor,
  HorizontalRule,
  Strong,
  Emphasis,
} from '@/components/markdown/MarkdownComponents'

interface Doc {
  id: string
  section: string
  title: string
  slug: string
  content: string
  tags: string[]
  sortOrder: number
  createdAt: string
  updatedAt: string
}

interface GroupedDocs {
  [section: string]: Doc[]
}

// Extract headings from markdown for TOC
interface TocItem {
  id: string
  text: string
  level: number
}

function extractToc(content: string): TocItem[] {
  const headingRegex = /^(#{1,3})\s+(.+)$/gm
  const toc: TocItem[] = []
  let match

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length
    const text = match[2].trim()
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
    toc.push({ id, text, level })
  }

  return toc
}

// Format section name
function formatSectionName(section: string): string {
  return section
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function DocsPage() {
  const orgContext = useOutletContext<OrganizationContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [docs, setDocs] = useState<Doc[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDoc, setSelectedDoc] = useState<Doc | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [activeHeading, setActiveHeading] = useState('')

  // Fetch docs
  useEffect(() => {
    const fetchDocs = async () => {
      if (!orgContext?.organizationId) return

      try {
        const db = getFirebaseDb()
        const docsRef = collection(db, 'organizations', orgContext.organizationId, 'docs')
        const q = query(docsRef, orderBy('section'), orderBy('sortOrder'))
        const snapshot = await getDocs(q)

        const docsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || '',
          updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || '',
        })) as Doc[]

        setDocs(docsData)

        // Expand all sections by default
        const sections = new Set(docsData.map(d => d.section))
        setExpandedSections(sections)

        // If there's a doc slug in the URL, select it
        const docSlug = searchParams.get('doc')
        if (docSlug) {
          const doc = docsData.find((d) => d.slug === docSlug)
          if (doc) setSelectedDoc(doc)
        } else if (docsData.length > 0) {
          // Select first doc by default
          setSelectedDoc(docsData[0])
          setSearchParams({ doc: docsData[0].slug })
        }
      } catch (error) {
        console.error('Error fetching docs:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDocs()
  }, [orgContext?.organizationId])

  // Group docs by section
  const groupedDocs = useMemo(() => {
    return docs.reduce((acc, doc) => {
      if (!acc[doc.section]) {
        acc[doc.section] = []
      }
      acc[doc.section].push(doc)
      return acc
    }, {} as GroupedDocs)
  }, [docs])

  // Filter docs by search
  const filteredGroupedDocs = useMemo(() => {
    if (!searchQuery.trim()) return groupedDocs

    const query = searchQuery.toLowerCase()
    const filtered: GroupedDocs = {}

    Object.entries(groupedDocs).forEach(([section, sectionDocs]) => {
      const matchingDocs = sectionDocs.filter(
        doc =>
          doc.title.toLowerCase().includes(query) ||
          doc.content.toLowerCase().includes(query)
      )
      if (matchingDocs.length > 0) {
        filtered[section] = matchingDocs
      }
    })

    return filtered
  }, [groupedDocs, searchQuery])

  // Extract TOC from selected doc
  const toc = useMemo(() => {
    if (!selectedDoc) return []
    return extractToc(selectedDoc.content)
  }, [selectedDoc])

  // Set up intersection observer for active heading tracking
  useEffect(() => {
    if (!selectedDoc) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id)
          }
        })
      },
      { rootMargin: '-100px 0px -80% 0px' }
    )

    const timer = setTimeout(() => {
      const headings = document.querySelectorAll('.doc-content h1, .doc-content h2, .doc-content h3')
      headings.forEach((heading) => observer.observe(heading))
    }, 100)

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [selectedDoc])

  const handleSelectDoc = (doc: Doc) => {
    setSelectedDoc(doc)
    setSearchParams({ doc: doc.slug })
    setActiveHeading('')
    // Scroll to top of content
    document.querySelector('.doc-content')?.scrollTo(0, 0)
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
      }
      return next
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Empty state
  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-center">
        <FileText className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">No documentation yet</h2>
        <p className="text-muted-foreground max-w-md">
          Documentation for your organization will appear here once it's been added.
        </p>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-140px)] -mx-6 -mt-6">
      {/* Left Sidebar - Navigation */}
      <div className="w-72 border-r bg-muted/30 flex flex-col">
        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search docs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-background"
            />
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1">
          <nav className="p-3">
            {Object.entries(filteredGroupedDocs).map(([section, sectionDocs]) => (
              <div key={section} className="mb-2">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section)}
                  className="flex items-center justify-between w-full px-2 py-1.5 text-sm font-medium text-foreground hover:bg-accent rounded-md transition-colors"
                >
                  <span>{formatSectionName(section)}</span>
                  {expandedSections.has(section) ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {/* Section Items */}
                {expandedSections.has(section) && (
                  <div className="mt-1 ml-2 border-l border-border">
                    {sectionDocs.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => handleSelectDoc(doc)}
                        className={cn(
                          'block w-full text-left px-3 py-1.5 text-sm transition-colors rounded-r-md -ml-px border-l-2',
                          selectedDoc?.id === doc.id
                            ? 'border-primary bg-primary/10 text-primary font-medium'
                            : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
                        )}
                      >
                        {doc.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </ScrollArea>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Doc Content */}
        <ScrollArea className="flex-1">
          {selectedDoc ? (
            <div className="max-w-4xl mx-auto px-8 py-8">
              {/* Doc Header */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-xs capitalize">
                    {formatSectionName(selectedDoc.section)}
                  </Badge>
                  {selectedDoc.tags?.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <h1 className="text-3xl font-bold tracking-tight mb-2">{selectedDoc.title}</h1>
                {selectedDoc.updatedAt && (
                  <p className="text-sm text-muted-foreground">
                    Last updated {new Date(selectedDoc.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>

              {/* Markdown Content */}
              <div className="doc-content max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Headings
                    h1: ({ children }) => <Heading level={1}>{children}</Heading>,
                    h2: ({ children }) => <Heading level={2}>{children}</Heading>,
                    h3: ({ children }) => <Heading level={3}>{children}</Heading>,

                    // Code
                    code: ({ className, children }) => {
                      const isInline = !className
                      if (isInline) {
                        return <InlineCode>{children}</InlineCode>
                      }
                      return (
                        <CodeBlock className={className}>
                          {String(children)}
                        </CodeBlock>
                      )
                    },
                    pre: ({ children }) => <>{children}</>,

                    // Tables
                    table: ({ children }) => <Table>{children}</Table>,
                    thead: ({ children }) => <TableHead>{children}</TableHead>,
                    tbody: ({ children }) => <TableBody>{children}</TableBody>,
                    tr: ({ children }) => <TableRow>{children}</TableRow>,
                    th: ({ children }) => <TableHeader>{children}</TableHeader>,
                    td: ({ children }) => <TableCell>{children}</TableCell>,

                    // Lists
                    ul: ({ children }) => <UnorderedList>{children}</UnorderedList>,
                    ol: ({ children }) => <OrderedList>{children}</OrderedList>,
                    li: ({ children }) => <ListItem>{children}</ListItem>,

                    // Block elements
                    p: ({ children }) => <Paragraph>{children}</Paragraph>,
                    blockquote: ({ children }) => <Blockquote>{children}</Blockquote>,
                    hr: () => <HorizontalRule />,

                    // Inline elements
                    a: ({ children, href }) => <Anchor href={href}>{children}</Anchor>,
                    strong: ({ children }) => <Strong>{children}</Strong>,
                    em: ({ children }) => <Emphasis>{children}</Emphasis>,
                  }}
                >
                  {selectedDoc.content}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              Select a document from the sidebar
            </div>
          )}
        </ScrollArea>

        {/* Right Sidebar - TOC */}
        {selectedDoc && toc.length > 0 && (
          <div className="w-56 border-l bg-muted/20 hidden lg:block">
            <div className="sticky top-0 p-4">
              <h4 className="font-medium text-sm mb-3 text-foreground">On this page</h4>
              <nav className="space-y-1">
                {toc.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
                    }}
                    className={cn(
                      'block text-[13px] py-1 transition-colors hover:text-foreground',
                      item.level === 2 && 'pl-0',
                      item.level === 3 && 'pl-3',
                      activeHeading === item.id
                        ? 'text-primary font-medium'
                        : 'text-muted-foreground'
                    )}
                  >
                    {item.text}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
